import {Alert, Button, Stack, Text, Title} from "@mantine/core";
import {useQuery} from "@tanstack/react-query";
import {ReactNode} from "react";
import {useNavigate, useParams} from "react-router";
import {WarningIcon} from "@phosphor-icons/react";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import api from "../../../utils/api.ts";
import ProductionModuleContext from "./ProductionModuleContext.tsx";
import ProductionModuleNav from "./ProductionModuleNav.tsx";

interface ModuleStatusResponse {
    id?: string;
    label?: string;
    isOwner?: boolean;
    billing?: {
        expired: boolean;
    };
}

/**
 * Wraps the data tabs of a production module. While a module subscription is
 * expired the tabs stay visible (via the nav) but their content is not rendered;
 * the user is pointed at the Settings tab to renew. The backend independently
 * blocks the underlying data endpoints, so this is a UX guard, not the only one.
 */
export default function ProductionModuleGuard({children}: {children: ReactNode}) {
    const {moduleId} = useParams<{moduleId?: string}>();
    const navigate = useNavigate();

    const statusQuery = useQuery<ModuleStatusResponse>({
        queryKey: ['prod-module', moduleId, 'settings'],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/settings`)).data as ModuleStatusResponse,
        enabled: !!moduleId,
    });

    if (!moduleId) {
        return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
    }

    if (statusQuery.isPending) {
        return <LoaderScreen message="Loading module"/>;
    }

    if (statusQuery.data?.billing?.expired) {
        return (
            <Page>
                <Stack gap="md">
                    <Title order={1}>Production Module</Title>
                    <ProductionModuleNav moduleId={moduleId}/>
                    <Alert color="red" icon={<WarningIcon/>} title="Subscription expired">
                        <Stack gap="sm" align="flex-start">
                            <Text>
                                This module&apos;s subscription has expired. Its tabs are locked and its entities are no
                                longer refreshed until the subscription is renewed.
                            </Text>
                            {statusQuery.data?.isOwner ? (
                                <Button onClick={() => navigate(`/modules/production/${moduleId}/settings`)}>
                                    Go to Settings
                                </Button>
                            ) : null}
                        </Stack>
                    </Alert>
                </Stack>
            </Page>
        );
    }

    return (
        <ProductionModuleContext.Provider
            value={{
                id: moduleId,
                label: statusQuery.data?.label ?? '',
                isOwner: statusQuery.data?.isOwner ?? false,
                expired: false,
            }}
        >
            {children}
        </ProductionModuleContext.Provider>
    );
}
